import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { login, register } from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Music, Loader2, ArrowLeft } from 'lucide-react';
import useSEO from '../hooks/useSEO';

const Login = () => {
  const navigate = useNavigate();
  
  // SEO Meta Tags
  useSEO({
    title: 'Connexion | DJ Nomad',
    description: 'Connectez-vous à DJ Nomad pour gérer la musique de vos événements.',
    canonical: 'https://djnomad.app/login',
    robots: 'noindex, follow'
  });
  
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = isLogin 
        ? await login({ email: formData.email, password: formData.password })
        : await register(formData);
      
      // Store user info only (not sensitive - HttpOnly cookies handle tokens)
      const user = response.data.user;
      localStorage.setItem('user', JSON.stringify(user));
      
      toast.success(isLogin ? 'Connexion réussie !' : 'Compte créé avec succès !');
      
      // Redirection intelligente selon le rôle
      if (user.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Une erreur est survenue';
      
      if (error.response?.status === 429) {
        toast.error('Trop de tentatives. Veuillez réessayer dans quelques minutes.');
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-violet-600 via-violet-500 to-purple-500 relative overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>
      
      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 flex flex-col items-center gap-4 shadow-2xl">
            <Loader2 className="w-12 h-12 text-violet-600 animate-spin" />
            <p className="text-gray-900 font-semibold text-lg">
              {isLogin ? 'Connexion en cours...' : 'Création du compte...'}
            </p>
            <p className="text-violet-600 text-sm">Veuillez patienter</p>
          </div>
        </div>
      )}

      {/* Back to home link */}
      <a 
        href="/" 
        className="absolute top-4 left-4 flex items-center gap-2 text-white/80 hover:text-white transition-colors z-10"
      >
        <ArrowLeft className="w-5 h-5" />
        <span className="text-sm font-medium">Accueil</span>
      </a>
      
      <div className="w-full max-w-md relative z-10">
        {/* Logo & Title */}
        <div className="text-center mb-8">
          <div className="inline-block p-4 rounded-2xl bg-white/20 backdrop-blur-sm mb-4 shadow-lg">
            <Music className="w-12 h-12 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-white mb-2">
            DJ Nomad
          </h1>
          <p className="text-white/80 text-sm">
            Automatisez la musique de vos événements
          </p>
        </div>

        {/* Form Card - Light Theme */}
        <div className="bg-white rounded-2xl p-8 shadow-2xl">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            {isLogin ? 'Connexion' : 'Créer un compte'}
          </h2>
          
          <form onSubmit={handleSubmit} className="space-y-5">
            {!isLogin && (
              <div>
                <Label htmlFor="name" className="text-gray-700 text-sm mb-2 block font-medium">
                  Nom complet
                </Label>
                <Input
                  id="name"
                  data-testid="register-name-input"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="bg-gray-50 border border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-violet-500 focus:ring-violet-500/30 h-12 transition-all duration-200 rounded-xl"
                  placeholder="Jean Dupont"
                  required
                />
              </div>
            )}

            <div>
              <Label htmlFor="email" className="text-gray-700 text-sm mb-2 block font-medium">
                Email
              </Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="bg-gray-50 border border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-violet-500 focus:ring-violet-500/30 h-12 transition-all duration-200 rounded-xl"
                placeholder="votre@email.com"
                required
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-gray-700 text-sm mb-2 block font-medium">
                Mot de passe
              </Label>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="bg-gray-50 border border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-violet-500 focus:ring-violet-500/30 h-12 transition-all duration-200 rounded-xl"
                placeholder="••••••••"
                required
              />
            </div>

            <Button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-violet-500/25 transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2"
            >
              {loading && <Loader2 className="w-5 h-5 animate-spin" />}
              {loading ? 'Chargement...' : (isLogin ? 'Se connecter' : "S'inscrire")}
            </Button>
          </form>

          {/* Toggle */}
          <div className="mt-6 text-center">
            <button
              data-testid="toggle-auth-mode-button"
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              className="text-violet-600 hover:text-violet-700 text-sm transition-colors duration-200 font-medium"
            >
              {isLogin ? "Pas encore de compte ? S'inscrire" : 'Déjà un compte ? Se connecter'}
            </button>
          </div>
        </div>

        {/* Footer text */}
        <p className="text-center text-white/60 text-xs mt-6">
          En vous connectant, vous acceptez nos{' '}
          <a href="/terms" className="text-white/80 hover:text-white underline">CGU</a>
          {' '}et notre{' '}
          <a href="/privacy" className="text-white/80 hover:text-white underline">Politique de confidentialité</a>
        </p>
      </div>
    </div>
  );
};

export default Login;
